// Режим помощника. Панель, а не модалка: переключение обратимо и ничего необратимого за
// собой не тянет (правило 4).
//
// ## Переключатель — на уровне УЧЁТНОЙ ЗАПИСИ, и это сказано словами
//
// Сценарий 99 требует именно этого: режим задаётся записи площадки, а не отдельной переписке.
// У заказчика записей три, и режим у них может быть разный. Человек, открывший панель из
// конкретного диалога, обязан узнать, что меняет он не этот диалог, — иначе он выключит
// помощника «на одном разговоре» и удивится через час, обнаружив молчание во всех.
//
// ## Пустая вилка оплаты — законный ответ, а не незаполненность (правило 7)
//
// «Рамок нет» означает, что помощник не называет сумм вовсе: сторож исходящего пропускает
// число, только если оно попадает в вилку. Это решение, а не пропуск, и подписано оно так же.
//
// Обратная сторона: убрать уже заданную границу через этот экран нельзя. Сервер понимает
// пустое поле как «не менять» (`НастройкиПомощникаIn`), и молчаливое «ничего не произошло»
// после сохранения было бы враньём. Поэтому пустое поле поверх заданного значения — это
// названная причина и шаг, а не тихий отказ (правило 10).

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiErrorWarningLine } from '@remixicon/react';

import { formatKopecks, formatRubles } from '@/shared/money/format';
import { moneyErrorText, parseRublesToKopecks } from '@/shared/money/parse';
import { formatDateDMY, formatTimeHM, parseMoment } from '@/shared/time/format';
import * as Button from '@/shared/ui/button';
import { CheckboxField } from '@/shared/ui/checkbox-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';

import type {
  AssistantMode,
  AssistantSettings,
  AssistantSettingsIn,
  AssistantSpeechMode,
} from '../api/dialogs';
import { ASSISTANT_MODES, ASSISTANT_SPEECH_MODES } from '../api/dialogs';
import { GateNote, NoPermissionNote, useCan } from '../components/gates';
import {
  ASSISTANT_MODE_NOTES,
  ASSISTANT_MODE_TITLES,
  ASSISTANT_SPEECH_NOTES,
  ASSISTANT_SPEECH_TITLES,
} from '../dictionaries';
import { useAssistantSettingsQuery, useSaveAssistantSettingsMutation } from '../queries';

/** Менять режим помощника и вести библиотеку ответов. */
const CONFIGURE = 'hiring.assistant.configure';

export function AssistantSettingsPanel({
  accountId,
  onClose,
}: {
  accountId: string;
  onClose: () => void;
}) {
  const query = useAssistantSettingsQuery();

  return (
    <ScreenState
      data={query.data}
      error={query.error}
      subject='режим помощника по учётной записи'
      isEmpty={(данные) =>
        данные.items.find((строка) => строка.account_id === accountId) === undefined
      }
      onRetry={() => void query.refetch()}
      loading={
        <div className='flex flex-col gap-3'>
          <Skeleton className='h-4 w-2/3' />
          <Skeleton className='h-16 w-full' />
          <Skeleton className='h-16 w-full' />
        </div>
      }
      empty={
        <p className='text-paragraph-sm text-text-sub-600'>
          Такой учётной записи площадки в системе нет: возможно, её удалили, пока панель была
          открыта. Вернитесь к списку записей в представлении «Площадка».
        </p>
      }
    >
      {(данные) => {
        const запись = данные.items.find((строка) => строка.account_id === accountId);
        if (запись === undefined) {
          return null;
        }
        // `key` по записи: смена учётной записи — это другая форма с другими значениями, а не
        // та же с подменёнными. Без него в полях остались бы цифры прежней записи.
        return <Форма key={запись.account_id} запись={запись} onClose={onClose} />;
      }}
    </ScreenState>
  );
}

interface СостояниеФормы {
  mode: string;
  speech: string;
  /** Рубли, как их пишет человек: «3 500», «3500,50». */
  payMin: string;
  payMax: string;
  /** Проценты: сервер хранит долю от нуля до единицы, человеку она ничего не говорит. */
  confidence: string;
  silence: boolean;
}

type ИмяПоля = 'mode' | 'speech' | 'payMin' | 'payMax' | 'confidence';

/** Копейки в поле ввода: тем же видом, каким деньги показываются везде (правило 5). */
function вПоле(копейки: number | null): string {
  return копейки === null ? '' : formatKopecks(копейки);
}

function Форма({ запись, onClose }: { запись: AssistantSettings; onClose: () => void }) {
  const разрешено = useCan(CONFIGURE);
  const mutation = useSaveAssistantSettingsMutation(запись.account_id);

  const [форма, setФорма] = useState<СостояниеФормы>(() => ({
    mode: запись.mode,
    speech: запись.speech_mode,
    payMin: вПоле(запись.pay_min_kopecks),
    payMax: вПоле(запись.pay_max_kopecks),
    confidence: String(Math.round(запись.confidence_threshold * 100)),
    silence: запись.silence_after_human,
  }));
  const [ошибки, setОшибки] = useState<Partial<Record<ИмяПоля, string>>>({});
  const [заметка, setЗаметка] = useState<string | null>(null);

  function поправить(часть: Partial<СостояниеФормы>) {
    setФорма((текущее) => ({ ...текущее, ...часть }));
  }

  function сохранить(событие: FormEvent<HTMLFormElement>) {
    событие.preventDefault();
    if (!разрешено) {
      setЗаметка(
        'Менять режим помощника может администратор системы: это отдельное право, оно не совпадает с правом вести переписку. Попросите выдать его.',
      );
      return;
    }
    if (mutation.isPending) {
      setЗаметка('Настройка уже сохраняется, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }

    const найдено: Partial<Record<ИмяПоля, string>> = {};

    if (!(ASSISTANT_MODES as readonly string[]).includes(форма.mode)) {
      найдено.mode = 'Выберите режим — иначе непонятно, что делать помощнику по этой записи.';
    }

    if (!(ASSISTANT_SPEECH_MODES as readonly string[]).includes(форма.speech)) {
      найдено.speech = 'Выберите, чем помощник говорит: готовыми формулировками или своими словами.';
    }

    const нижняя = граница(форма.payMin, запись.pay_min_kopecks, 'нижнюю границу');
    if (нижняя.ошибка !== undefined) {
      найдено.payMin = нижняя.ошибка;
    }
    const верхняя = граница(форма.payMax, запись.pay_max_kopecks, 'верхнюю границу');
    if (верхняя.ошибка !== undefined) {
      найдено.payMax = верхняя.ошибка;
    }

    // Сверяем то, что окажется на сервере ПОСЛЕ сохранения: пустое поле означает «не менять»,
    // то есть прежнее значение остаётся и участвует в проверке наравне с новым.
    const итогНижней = нижняя.значение ?? запись.pay_min_kopecks;
    const итогВерхней = верхняя.значение ?? запись.pay_max_kopecks;
    if (итогНижней !== null && итогВерхней !== null && итогНижней > итогВерхней) {
      найдено.payMin = `Нижняя граница больше верхней: ${formatRubles(итогНижней)} против ${formatRubles(итогВерхней)}. Поменяйте их местами.`;
    }

    const порог = вДолю(форма.confidence);
    if (порог === null) {
      найдено.confidence =
        'Порог пишется числом процентов от 0 до 100: например, 70. Ниже него помощник не отвечает сам.';
    }

    if (Object.keys(найдено).length > 0) {
      setОшибки(найдено);
      return;
    }

    setОшибки({});
    setЗаметка(null);
    const тело: AssistantSettingsIn = {
      mode: форма.mode as AssistantMode,
      speech_mode: форма.speech as AssistantSpeechMode,
      pay_min_kopecks: нижняя.значение,
      pay_max_kopecks: верхняя.значение,
      confidence_threshold: порог,
      silence_after_human: форма.silence,
    };
    mutation.mutate(тело, { onSuccess: onClose });
  }

  const выбранный = форма.mode as AssistantMode;
  const пояснениеРежима = ASSISTANT_MODE_NOTES[выбранный] as string | undefined;
  const пояснениеРечи = ASSISTANT_SPEECH_NOTES[форма.speech as AssistantSpeechMode] as
    | string
    | undefined;
  const момент = parseMoment(запись.changed_at);
  const вилкаПуста = запись.pay_min_kopecks === null && запись.pay_max_kopecks === null;

  return (
    <form onSubmit={сохранить} noValidate className='flex flex-col gap-3'>
      {/* Сценарий 99 словами: человек пришёл сюда из конкретной переписки и обязан узнать,
          что меняет не её. */}
      <p className='text-paragraph-sm text-text-sub-600'>
        Режим действует на всю учётную запись «{запись.account_title}»: на все переписки по ней, и
        нынешние, и будущие. Своего режима у отдельного диалога нет.
      </p>

      {!разрешено && (
        <NoPermissionNote
          что='менять режим помощника'
          кто='Режим задаёт администратор системы — попросите его открыть право.'
        />
      )}

      <SelectField
        id='assistant-mode'
        label='Режим помощника'
        required
        value={форма.mode}
        onChange={(значение) => поправить({ mode: значение })}
        options={ASSISTANT_MODES.map((код) => ({ value: код, label: ASSISTANT_MODE_TITLES[код] }))}
        placeholder='Выберите режим'
        error={ошибки.mode}
        hint={пояснениеРежима}
      />

      <SelectField
        id='assistant-speech'
        label='Чем помощник говорит'
        required
        value={форма.speech}
        onChange={(значение) => поправить({ speech: значение })}
        options={ASSISTANT_SPEECH_MODES.map((код) => ({
          value: код,
          label: ASSISTANT_SPEECH_TITLES[код],
        }))}
        placeholder='Выберите способ'
        error={ошибки.speech}
        hint={пояснениеРечи}
      />

      <TextField
        id='assistant-pay-min'
        label='Вилка оплаты: от, рублей'
        inputMode='decimal'
        value={форма.payMin}
        onChange={(значение) => поправить({ payMin: значение })}
        placeholder='3 500'
        error={ошибки.payMin}
      />

      <TextField
        id='assistant-pay-max'
        label='Вилка оплаты: до, рублей'
        inputMode='decimal'
        value={форма.payMax}
        onChange={(значение) => поправить({ payMax: значение })}
        placeholder='5 000'
        error={ошибки.payMax}
        hint={
          вилкаПуста
            ? 'Рамок нет — это законный ответ, а не незаполненность: пока вилки нет, помощник не называет сумм вовсе, даже если их спрашивают.'
            : 'Число вне вилки помощник наружу не выпустит: сумму за её пределами сторож исходящего останавливает.'
        }
      />

      <TextField
        id='assistant-confidence'
        label='Порог уверенности, процентов'
        required
        inputMode='decimal'
        value={форма.confidence}
        onChange={(значение) => поправить({ confidence: значение })}
        placeholder='70'
        error={ошибки.confidence}
        hint='Ниже этого порога помощник не отвечает сам, а передаёт разговор человеку. Чем выше порог, тем чаще он обращается к рекрутёру.'
      />

      <CheckboxField
        id='assistant-silence'
        label='Молчать после сообщения человека'
        checked={форма.silence}
        onCheckedChange={(значение) => поправить({ silence: значение })}
        hint='Отметка держит помощника молчащим в той переписке, в которую вмешался сотрудник, — до нажатия «Вернуть помощника».'
      />

      <p className='text-paragraph-xs text-text-sub-600'>
        {момент === null
          ? 'Режим ещё ни разу не меняли: он такой с заведения учётной записи.'
          : `Последнее изменение: ${formatDateDMY(момент)}, ${formatTimeHM(момент)}.`}
      </p>

      {mutation.isError && <ErrorAlert error={mutation.error} />}

      {заметка !== null && <GateNote>{заметка}</GateNote>}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {mutation.isPending ? 'Сохраняем…' : 'Сохранить режим'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>

      {!разрешено && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          Форму можно заполнить и посмотреть, но сохранение не пройдёт: право на режим помощника
          выдаётся отдельно от права вести переписку.
        </Hint.Root>
      )}
    </form>
  );
}

/**
 * Граница вилки из поля ввода. Три исхода, и все три разные:
 * значение — менять на новое, `null` — не трогать, ошибка — сказать, почему не сохранится.
 */
function граница(
  введено: string,
  нынешнее: number | null,
  чего: string,
): { значение: number | null; ошибка?: string } {
  if (введено.trim() === '') {
    if (нынешнее === null) {
      return { значение: null };
    }
    return {
      значение: null,
      ошибка: `Убрать уже заданную границу отсюда нельзя: пустое поле означает «оставить как есть», и после сохранения там осталось бы ${formatRubles(нынешнее)}. Оставьте прежнее значение или задайте новое.`,
    };
  }
  const разобрано = parseRublesToKopecks(введено);
  if (!разобрано.ok) {
    return { значение: null, ошибка: moneyErrorText(разобрано.reason, чего) };
  }
  return { значение: разобрано.value };
}

/** Проценты из поля в долю от нуля до единицы. Мусор и выход за края — null. */
function вДолю(введено: string): number | null {
  const очищено = введено.trim().replace(',', '.');
  if (очищено === '') {
    return null;
  }
  const число = Number(очищено);
  if (!Number.isFinite(число) || число < 0 || число > 100) {
    return null;
  }
  // Считаем целыми: 72,5 процента через плавающую точку дало бы 0.7250000000000001.
  return Math.round(число * 100) / 10000;
}
